import React, { useEffect, useRef, useState } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { getMapsToken } from '../services/MapsService';
import LocationAutocomplete from './LocationAutocomplete';
import { ILocation } from '../interfaces/heavyDutyMachinery/ILocation';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { CORAL, TEAL } from '../theme/creamyTokens';

const { Text } = Typography;

// Same "road" style + desaturating canvas filter as SpacesMap.tsx -- these
// two small consts aren't exported there (that file has no public surface
// for them), so they're repeated here rather than reworking an unrelated
// component's exports for two literals.
const MAP_CANVAS_FILTER = 'saturate(0.55) brightness(1.10)';
const ROAD_STYLE = 'road';
const DEFAULT_CENTER: [number, number] = [-3.7038, 40.4168]; // Madrid
const PIN_ZOOM = 15;

export interface DeliveryLocationMapProps {
  /** Current pin position, if one is already set. */
  latitude?: number;
  longitude?: number;
  /** Falls back center when no pin is set yet -- typically the machine's own location, so the map opens already looking at the right area. */
  centerLatitude?: number;
  centerLongitude?: number;
  onChange: (latitude: number, longitude: number) => void;
  disabled?: boolean;
}

/**
 * Draggable-pin location picker for the checkout delivery step. The publish
 * wizard's own "map" (LocationAutocomplete + StaticMap) is a text search
 * plus a non-interactive server-rendered image -- neither supports dragging
 * a pin, so there's nothing literal to reuse for that specific interaction.
 * What IS reused: LocationAutocomplete itself (verbatim, same address-search
 * component the wizard uses) for typing an address, and azure-maps-control,
 * the same interactive map library SpacesMap.tsx already depends on for the
 * search-results map -- so this adds a new small component, not a new
 * mapping dependency. Tap-to-place AND drag-to-adjust both work from touch,
 * not just mouse (azure-maps-control's HtmlMarker forwards pointer events).
 */
const DeliveryLocationMap: React.FC<DeliveryLocationMapProps> = ({
  latitude,
  longitude,
  centerLatitude,
  centerLongitude,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<atlas.Map | undefined>(undefined);
  const markerRef = useRef<atlas.HtmlMarker | undefined>(undefined);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  const placeMarker = (position: [number, number], map: atlas.Map) => {
    if (markerRef.current) {
      markerRef.current.setOptions({ position });
    } else {
      const marker = new atlas.HtmlMarker({
        position,
        draggable: !disabled,
        color: CORAL,
      });
      map.markers.add(marker);
      map.events.add('dragend', marker, () => {
        const [lng, lat] = marker.getOptions().position as [number, number];
        onChangeRef.current(lat, lng);
      });
      markerRef.current = marker;
    }
  };

  useEffect(() => {
    let cancelled = false;
    let map: atlas.Map | undefined;

    const init = async () => {
      const mapsToken = await getMapsToken().catch(() => undefined);
      if (cancelled) return;
      if (!mapsToken || !containerRef.current) {
        setUnavailable(true);
        setLoading(false);
        return;
      }

      const initialCenter: [number, number] =
        longitude != null && latitude != null
          ? [longitude, latitude]
          : centerLongitude != null && centerLatitude != null
            ? [centerLongitude, centerLatitude]
            : DEFAULT_CENTER;

      map = new atlas.Map(containerRef.current, {
        center: initialCenter,
        zoom: latitude != null || centerLatitude != null ? PIN_ZOOM : 5,
        language: 'es-ES',
        style: ROAD_STYLE,
        showLogo: false,
        showFeedbackLink: false,
        authOptions: {
          authType: atlas.AuthenticationType.anonymous,
          clientId: mapsToken.clientId,
          getToken: (resolve, reject) => {
            getMapsToken()
              .then((refreshed) => (refreshed ? resolve(refreshed.token) : reject(new Error('No map token'))))
              .catch(reject);
          },
        },
      });
      mapRef.current = map;

      map.events.add('ready', () => {
        if (cancelled || !map) return;
        const canvas = containerRef.current?.querySelector('canvas');
        if (canvas) (canvas as HTMLCanvasElement).style.filter = MAP_CANVAS_FILTER;

        if (latitude != null && longitude != null) placeMarker([longitude, latitude], map);

        setLoading(false);
      });

      // Tap (or click) anywhere on the map drops/moves the pin there --
      // the easiest interaction on a phone, dragging the pin afterwards
      // just fine-tunes it.
      if (!disabled) {
        map.events.add('click', (e: atlas.MapMouseEvent) => {
          if (!e.position || !map) return;
          const [lng, lat] = e.position;
          placeMarker([lng, lat], map);
          onChangeRef.current(lat, lng);
        });
      }
    };

    init();

    return () => {
      cancelled = true;
      markerRef.current = undefined;
      map?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A location picked from the address search box (typed, not dragged/
  // tapped) re-centers the map and (re)places the pin there.
  const handleSelectLocation = (location: ILocation) => {
    if (location.latitude == null || location.longitude == null) return;
    const map = mapRef.current;
    if (!map) return;
    const position: [number, number] = [location.longitude, location.latitude];
    map.setCamera({ center: position, zoom: PIN_ZOOM });
    placeMarker(position, map);
    onChangeRef.current(location.latitude, location.longitude);
  };

  if (unavailable) {
    return (
      <Text type="secondary" style={{ fontSize: 13 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_LOCATION_HELP)}
      </Text>
    );
  }

  return (
    <div>
      <LocationAutocomplete
        disabled={disabled}
        includeCountryInLabel
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_LOCATION_LABEL)}
        onSelectLocation={handleSelectLocation}
        style={{ marginBottom: 8, width: '100%' }}
      />
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_LOCATION_HELP)}
      </Text>
      <div style={{ position: 'relative', borderRadius: 18, overflow: 'hidden' }}>
        <div
          ref={containerRef}
          // touch-action: none stops the browser's own scroll/pan gesture
          // from fighting the map SDK's own touch handling for panning the
          // map and dragging the pin -- without it, a finger drag on a
          // mobile browser scrolls the page instead of moving the pin.
          style={{ width: '100%', height: 260, touchAction: 'none', background: TEAL + '10' }}
        />
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="small" />
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryLocationMap;
